import { toast } from "@assetwatch/ui";
import {
  confirmSignIn,
  signIn,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { useEffect, useState } from "react";
import type {
  AuthType,
  CustomLoginFormMessages,
  CustomLoginFormProps,
  StatusTone,
} from "./CustomLoginForm";
import { DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES } from "./defaultMessages";
import {
  consumeSsoRedirect,
  getErrorMessage,
  getNewPasswordErrorMessage,
  INITIAL_PASSWORD_VALIDATION,
  isIncorrectCredentialsError,
  isNewPasswordFieldError,
  isSafeRedirect,
  saveSsoRedirect,
  validateEmail,
} from "./loginUtils";

export function useLoginFlow({
  authenticate,
  forgotPasswordHref,
  initialEmail = "",
  initialStatusMessage,
  isAuthenticated,
  lookupLoginMethod,
  messages: messagesProp,
  onNavigate,
  passwordUpdated = false,
  redirectTo = "/",
}: CustomLoginFormProps) {
  const messages: CustomLoginFormMessages = {
    ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES,
    ...messagesProp,
    headings: {
      ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.headings,
      ...messagesProp?.headings,
    },
    actions: {
      ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.actions,
      ...messagesProp?.actions,
    },
    loading: {
      ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.loading,
      ...messagesProp?.loading,
    },
    copy: { ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.copy, ...messagesProp?.copy },
    status: {
      ...DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.status,
      ...messagesProp?.status,
    },
  };
  const safeRedirectTo = isSafeRedirect(redirectTo) ? redirectTo : "/";
  const [authType, setAuthType] = useState<AuthType>("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [firstSignIn, setFirstSignIn] = useState(false);
  const [identityProvider, setIdentityProvider] = useState<string>();
  const [isAccountCreated, setIsAccountCreated] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRememberMeEnabled, setIsRememberMeEnabled] = useState(false);
  const [isSsoRedirecting, setIsSsoRedirecting] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [passedPasswordHistory, setPassedPasswordHistory] = useState<
    boolean | undefined
  >(undefined);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordValidation, setPasswordValidation] = useState(
    INITIAL_PASSWORD_VALIDATION,
  );
  const [statusTone, setStatusTone] = useState<StatusTone>(
    initialStatusMessage
      ? "negative"
      : passwordUpdated
        ? "positive"
        : "informative",
  );
  const [statusMessage, setStatusMessage] = useState<string | null>(
    initialStatusMessage ??
      (passwordUpdated ? messages.status.passwordUpdatedSuccess : null),
  );

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberMeLogin");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setIsRememberMeEnabled(true);
    }
  }, []);

  useEffect(() => {
    setStatusTone(
      initialStatusMessage
        ? "negative"
        : passwordUpdated
          ? "positive"
          : "informative",
    );
    setStatusMessage(
      initialStatusMessage ??
        (passwordUpdated ? messages.status.passwordUpdatedSuccess : null),
    );
  }, [
    initialStatusMessage,
    messages.status.passwordUpdatedSuccess,
    passwordUpdated,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      onNavigate(consumeSsoRedirect() ?? safeRedirectTo);
    }
  }, [isAuthenticated, onNavigate, safeRedirectTo]);

  function resetAuthState() {
    setAuthType("");
    setConfirmPassword("");
    setFirstSignIn(false);
    setIdentityProvider(undefined);
    setIsAccountCreated(false);
    setMfaCode("");
    setNewPassword("");
    setNewPasswordError(null);
    setPassword("");
    setPasswordError(null);
    setIsConfirmPasswordVisible(false);
    setIsNewPasswordVisible(false);
    setIsPasswordVisible(false);
    setPasswordValidation(INITIAL_PASSWORD_VALIDATION);
    setPassedPasswordHistory(undefined);
    setIsSsoRedirecting(false);
    setStatusMessage(null);
    setStatusTone("informative");
  }

  function isFormValid() {
    if (firstSignIn) {
      return (
        passwordValidation.hasUppercase &&
        passwordValidation.hasLowercase &&
        passwordValidation.hasNumber &&
        passwordValidation.hasSpecialCharacter &&
        passwordValidation.hasSufficientLength &&
        passwordValidation.matchesConfirm
      );
    }

    if (authType === "mfacode") {
      return mfaCode.length === 6;
    }

    return email.trim().length > 0 && password.length > 0;
  }

  async function startSsoRedirect(provider: string | undefined) {
    if (!provider) {
      setStatusTone("negative");
      setStatusMessage(messages.errors.identityProviderUndefined);
      return;
    }

    setIdentityProvider(provider);
    setIsLoading(true);
    setIsSsoRedirecting(true);
    setStatusMessage(null);
    setStatusTone("informative");

    try {
      await signOut();

      if (safeRedirectTo !== "/") {
        saveSsoRedirect(safeRedirectTo);
      }

      await signInWithRedirect({
        provider: { custom: provider },
        customState: JSON.stringify({ isWebLogin: "true" }),
      });
    } catch (error) {
      setIsSsoRedirecting(false);
      setStatusTone("negative");
      setStatusMessage(getErrorMessage(error, messages));
    } finally {
      setIsLoading(false);
    }
  }

  async function checkEmail() {
    const normalizedEmail = email.trim().toLowerCase();

    setPasswordError(null);

    if (!validateEmail(normalizedEmail)) {
      setEmailError(messages.errors.invalidEmail);
      return;
    }

    setEmailError(null);

    if (isRememberMeEnabled) {
      localStorage.setItem("rememberMeLogin", normalizedEmail);
    } else {
      localStorage.removeItem("rememberMeLogin");
    }

    try {
      setIsCheckingEmail(true);
      const result = await lookupLoginMethod(normalizedEmail);
      if (result.authType === "sso") {
        await startSsoRedirect(result.identityProvider);
      } else {
        setAuthType(result.authType);
      }
    } catch {
      setAuthType("basic");
      toast.error(messages.errors.network, {
        action: {
          label: messages.actions.retry ?? "Retry",
          onClick: () => {
            void checkEmail();
          },
        },
      });
    } finally {
      setIsCheckingEmail(false);
    }
  }

  async function handleSubmit() {
    if (firstSignIn) {
      if (newPassword !== confirmPassword) {
        return;
      }

      setIsLoading(true);
      try {
        setNewPasswordError(null);
        const result = await confirmSignIn({ challengeResponse: newPassword });
        const nextStep = result.nextStep.signInStep;

        if (nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          setAuthType("mfacode");
          setConfirmPassword("");
          setFirstSignIn(false);
          setMfaCode("");
          setNewPassword("");
          setPassword("");
          setPasswordValidation(INITIAL_PASSWORD_VALIDATION);
          setPassedPasswordHistory(undefined);
          setStatusMessage(null);
          setStatusTone("informative");
          return;
        }

        if (nextStep !== "DONE") {
          setStatusTone("negative");
          setStatusMessage(messages.errors.unsupportedSignInStep);
          return;
        }

        if (authType === "temporaryPassword") {
          setConfirmPassword("");
          setNewPassword("");
          setPassedPasswordHistory(true);
          setPassword("");
          setPasswordValidation(INITIAL_PASSWORD_VALIDATION);
          setStatusMessage(null);
          setIsAccountCreated(true);
        } else {
          setPassedPasswordHistory(true);
          setStatusTone("positive");
          setStatusMessage(
            messages.status.temporaryPasswordUpdatedSuccess ??
              DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.status
                .temporaryPasswordUpdatedSuccess ??
              "Temporary password updated successfully. Please log in with your new password.",
          );
          await new Promise((resolve) => setTimeout(resolve, 500));
          await signOut();
          resetAuthState();
        }
      } catch (error) {
        setPassedPasswordHistory(false);
        if (isNewPasswordFieldError(error)) {
          setNewPasswordError(getNewPasswordErrorMessage(error, messages));
          setStatusMessage(null);
        } else {
          setStatusTone("negative");
          setStatusMessage(getNewPasswordErrorMessage(error, messages));
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    setStatusMessage(null);
    setStatusTone("informative");

    if (authType === "mfacode") {
      try {
        const result = await confirmSignIn({ challengeResponse: mfaCode });

        if (result.nextStep.signInStep !== "DONE") {
          setStatusTone("negative");
          setStatusMessage(messages.errors.unsupportedSignInStep);
          return;
        }

        await authenticate();
      } catch {
        setStatusTone("negative");
        setStatusMessage(messages.errors.invalidMfaCode);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    await signOut();

    if (authType === "sso") {
      if (!identityProvider) {
        setStatusTone("negative");
        setStatusMessage(messages.errors.identityProviderUndefined);
        setIsLoading(false);
        return;
      }

      if (safeRedirectTo !== "/") {
        saveSsoRedirect(safeRedirectTo);
      }

      await signInWithRedirect({
        provider: { custom: identityProvider },
        customState: JSON.stringify({ isWebLogin: "true" }),
      });
      setIsLoading(false);
      return;
    }

    try {
      setPasswordError(null);
      const result = await signIn({
        username: email.trim().toLowerCase(),
        password,
        options: {
          clientMetadata: {
            isWebLogin: "true",
          },
        },
      });

      switch (result.nextStep.signInStep) {
        case "RESET_PASSWORD":
          setStatusTone("negative");
          setStatusMessage(messages.errors.passwordResetRequired);
          break;
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
          setFirstSignIn(true);
          break;
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
          setAuthType("mfacode");
          break;
        case "DONE":
          await authenticate();
          break;
        default:
          setStatusTone("negative");
          setStatusMessage(messages.errors.unsupportedSignInStep);
      }
    } catch (error) {
      console.error(
        "[CustomLoginForm.handleSubmit]",
        JSON.stringify({
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : undefined,
        }),
      );
      setStatusTone("negative");
      if (isIncorrectCredentialsError(error)) {
        setPasswordError(messages.errors.incorrectEmailOrPassword);
        setStatusMessage(null);
      } else {
        setStatusMessage(getErrorMessage(error, messages));
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function startMonitoring() {
    setIsLoading(true);
    try {
      await authenticate();
    } finally {
      setIsLoading(false);
    }
  }

  const forgotPasswordUrl =
    typeof forgotPasswordHref === "function"
      ? forgotPasswordHref(email.trim().toLowerCase())
      : forgotPasswordHref;
  const passwordVisibilityLabel = isPasswordVisible
    ? messages.accessibility.hidePassword
    : messages.accessibility.showPassword;
  const newPasswordVisibilityLabel = isNewPasswordVisible
    ? messages.accessibility.hidePassword
    : messages.accessibility.showPassword;
  const confirmPasswordVisibilityLabel = isConfirmPasswordVisible
    ? messages.accessibility.hidePassword
    : messages.accessibility.showPassword;
  const isTemporaryPasswordChallenge =
    authType === "temporaryPassword" && firstSignIn;

  return {
    authType,
    checkEmail,
    confirmPassword,
    confirmPasswordVisibilityLabel,
    email,
    emailError,
    firstSignIn,
    forgotPasswordUrl,
    handleSubmit,
    isAccountCreated,
    isCheckingEmail,
    isConfirmPasswordVisible,
    isFormValid,
    isLoading,
    isNewPasswordVisible,
    isPasswordVisible,
    isRememberMeEnabled,
    isSsoRedirecting,
    isTemporaryPasswordChallenge,
    messages,
    mfaCode,
    newPassword,
    newPasswordError,
    newPasswordVisibilityLabel,
    passedPasswordHistory,
    password,
    passwordError,
    passwordValidation,
    passwordVisibilityLabel,
    resetAuthState,
    setConfirmPassword,
    setEmail,
    setEmailError,
    setIsConfirmPasswordVisible,
    setIsNewPasswordVisible,
    setIsPasswordVisible,
    setIsRememberMeEnabled,
    setMfaCode,
    setNewPassword,
    setNewPasswordError,
    setPassedPasswordHistory,
    setPassword,
    setPasswordError,
    setPasswordValidation,
    startMonitoring,
    statusMessage,
    statusTone,
  };
}
